import { IPbjWarningSummary, TPbjFormat } from './types';

/**
 * Header / body readers for `GET /pbj`, split out from the saga so they can be tested against the
 * exact shapes the service returns rather than through a mocked request.
 */

/**
 * Axios v1 hands back an `AxiosHeaders` instance in the browser and a plain object in most tests,
 * and HTTP header names are case-insensitive either way. Read through both rather than trusting a
 * casing.
 */
export const readPbjHeader = (headers: unknown, name: string): string | undefined => {
  if (!headers || typeof headers !== 'object') return undefined;

  const bag = headers as Record<string, unknown> & { get?: (key: string) => unknown };
  if (typeof bag.get === 'function') {
    const viaGetter = bag.get(name);
    if (viaGetter !== undefined && viaGetter !== null) return String(viaGetter);
  }

  const wanted = name.toLowerCase();
  const key = Object.keys(bag).find((candidate) => candidate.toLowerCase() === wanted);
  return key === undefined ? undefined : String(bag[key]);
};

/**
 * `X-PBJ-Warning-Count` + `X-PBJ-Warnings`, present on both the 200 and the 404.
 *
 * Only numeric values survive. The header is documented as counts per code, but this is the single
 * point where anything from that header could reach the DOM, so anything that is not a plain
 * positive number is dropped rather than rendered — warning *detail* carries ids and this surface
 * is PHI-adjacent. A malformed header must also never cost the user their download, hence the
 * swallowed parse error.
 */
export const parsePbjWarnings = (headers: unknown): IPbjWarningSummary => {
  const rawCount = Number(readPbjHeader(headers, 'X-PBJ-Warning-Count'));
  const warningCount = Number.isFinite(rawCount) && rawCount > 0 ? Math.trunc(rawCount) : 0;

  const warnings: Record<string, number> = {};
  const rawWarnings = readPbjHeader(headers, 'X-PBJ-Warnings');

  if (rawWarnings) {
    try {
      const decoded = JSON.parse(rawWarnings);
      if (decoded && typeof decoded === 'object' && !Array.isArray(decoded)) {
        Object.keys(decoded).forEach((code) => {
          const value = (decoded as Record<string, unknown>)[code];
          //  Reject arrays, objects and strings outright: `Number([12])` is 12, so a details
          //  array with a single element would otherwise slip through as a count.
          if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return;
          warnings[code] = Math.trunc(value);
        });
      }
    } catch {
      //  Unparseable header — fall through with whatever the count header gave us.
    }
  }

  return { warningCount, warnings };
};

/**
 * The filename comes off `Content-Disposition` so the server stays the single source of truth for
 * it; the fallback only covers a proxy that strips the header.
 */
export const parsePbjFilename = (headers: unknown, fallback: string): string => {
  const disposition = readPbjHeader(headers, 'Content-Disposition') ?? '';

  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (encoded?.[1]) {
    try {
      return decodeURIComponent(encoded[1].trim());
    } catch {
      //  Bad percent-encoding; try the plain parameter below.
    }
  }

  const plain = /filename=(?:"([^"]+)"|([^;]+))/i.exec(disposition);
  const value = (plain?.[1] ?? plain?.[2])?.trim();
  return value ? value : fallback;
};

/** Filename used only when the server's Content-Disposition never arrives. */
export const fallbackPbjFilename = (year: number, quarter: number, format: TPbjFormat): string =>
  format === 'csv' ? `pbj_FFY${year}_Q${quarter}_review.csv` : `pbj_FFY${year}_Q${quarter}.zip`;

export const pbjContentTypeFor = (format: TPbjFormat): string =>
  format === 'csv' ? 'text/csv;charset=utf-8' : 'application/zip';

/**
 * `responseType: 'arraybuffer'` applies to error responses too, so the 404's JSON body arrives as
 * bytes and has to be decoded before its `code` can be read.
 */
export const decodePbjErrorBody = (data: unknown): Record<string, unknown> | null => {
  if (!data) return null;

  let text: string;
  if (typeof data === 'string') {
    text = data;
  } else if (data instanceof ArrayBuffer) {
    text = new TextDecoder('utf-8').decode(data);
  } else if (ArrayBuffer.isView(data)) {
    text = new TextDecoder('utf-8').decode(data as Uint8Array);
  } else if (typeof data === 'object') {
    //  Already-parsed JSON (some transports, and most test fixtures).
    return data as Record<string, unknown>;
  } else {
    return null;
  }

  try {
    const decoded = JSON.parse(text);
    return decoded && typeof decoded === 'object' ? (decoded as Record<string, unknown>) : null;
  } catch {
    //  Not JSON — an HTML error page from a proxy, for instance. Nothing to read.
    return null;
  }
};

/**
 * A timeout is the one failure the card has to name specifically, because the advice differs: retry
 * rather than report. Axios reports it as ECONNABORTED (and ETIMEDOUT on some adapters) with no
 * response attached.
 */
export const isPbjTimeoutError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  const axiosError = error as { code?: string; message?: string; response?: unknown };
  if (axiosError.response) return false;
  if (axiosError.code === 'ECONNABORTED' || axiosError.code === 'ETIMEDOUT') return true;
  return /timeout/i.test(axiosError.message ?? '');
};
